/**
 * The board, resumed from the server every time. Which screen the host lands on
 * is decided by what the draft really is: no recording, one still encoding, a
 * transcript waiting to be read, or a board. Nothing about it lives in this tab
 * except which card is open, so closing the tab and coming back shows the same
 * board.
 *
 * This is the only file under `src/board` that talks to the server. What it does
 * with each answer lives in `ops.ts` and `save.ts`, which are tested on their
 * own; this file is the wiring between them and the screen.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { VIDEO_ERROR_MSG } from "../../shared/config";
import type {
  BoardCard,
  AutomationSummary,
  BoardCardRequest,
  BoardState,
  BoardStateResponse,
} from "../../shared/types";
import { uploadReceipt } from "../billing/format";
import { editorFromDetail, type Editor } from "../components/FlowEditor";
import { api, ApiError } from "../lib/api";
import { moveLiveTime, timeSavedLine } from "./ChangeTime";
import { emailOf, withEmail, type FollowUpBinding } from "./followups";
import type { BoardBusy, BoardHost, MomentKind, ToastAction } from "./host";
import { needsTranscribe, runCardOp, runTranscribe, writeFollowUp } from "./ops";
import { takeTalk, type PickedTalk } from "./pending";
import { useToast } from "../components/ui/Toast";
import type { Colours as BrandColours } from "../setup/looks";
import { chapterRowsOf, StepChapters, type ChapterRow } from "./make/MakeChapters";
import { DesignAskCard, DesignChoice, type DesignAsk } from "./make/MakeDesign";
import { MakeFrame, type RailTalk } from "./make/MakeFrame";
import { StepWrite } from "./make/MakeLive";
import { StepBuild, StepNames, StepUpload, StepWork } from "./make/MakeSteps";
import { mmss } from "./make/time";
import { WalkScreen } from "./walk/WalkScreen";
import { SeasonMake } from "./season/SeasonMake";
import { SeasonReady } from "./season/SeasonMake";
import { SeasonWalk } from "./season/SeasonWalk";
import { useLayout } from "../components/season/layout";
import {
  keepPolling,
  resumePhase,
  sizeLabel,
  videoFailed,
  type BoardPhase,
} from "./progress";
import { BOARD_RELOADED, BoardSaveQueue } from "./save";
import { boardReducer, findCard, type BoardAction } from "./state";

/**
 * How often the draft is asked what it has become, while it is becoming it. The
 * waiting stage shows the server's own counters, so it is only as alive as this:
 * a piece encoded, a minute heard or a card written shows up within a beat.
 */
const POLL_MS = 1500;
/**
 * The encode states that are past the probe, so the recording's length is on the
 * row and worth one ask. Before these there is nothing to ask for.
 */
const PROBED_STATES: readonly string[] = [
  "transcoding",
  "assembling",
  "poster",
  "transcribing",
  "ready",
];
/** How long after a save the board asks the server what it now thinks of it. */
const REFRESH_MS = 1200;
/**
 * The boards that have already asked for their stills again, in this tab. A
 * recording made before the strip had a wave has no picture to draw, and the ask
 * that draws one is minutes of work: once per board is once too often to repeat.
 */
const STILLS_ASKED = new Set<string>();
/** How long typing in a follow-up settles before the row is written. */
/** How long a card the producer just rewrote stays marked as changed. */
const FRESH_MS = 2600;
/** How many times an operation writes what waits before it goes; a change made during a write goes on the next. */
const FLUSH_TRIES = 3;
/** The last change would not save, so an operation now would leave on a board the server does not have. */
const NOT_SAVED_YET = "Your last change is not saved yet. Try again in a moment.";

const BLANK: BoardState = {
  v: 1,
  brand: null,
  arrive: { where: "br", pause: false, stay: "45" },
  slug: "",
  workingTitle: "",
  page: [],
  room: [],
  mail: [],
  trash: [],
};

interface FollowState {
  automationId: string;
  editor: Editor | null;
  /** The editor as the row last accepted it, so a trigger change can be spotted. */
  saved: Editor | null;
  error: string;
  saving: boolean;
}

/** Words the row has not accepted yet: the editor differs from what it last took. */
function followDirty(f: FollowState | null | undefined): boolean {
  return !!f?.editor && !!f.saved && JSON.stringify(f.editor) !== JSON.stringify(f.saved);
}

/** What the producer could not do on the last read, when the server says. */
function warningsOf(server: BoardStateResponse | null): string[] {
  return server?.warnings ?? [];
}

export function BoardView() {
  const params = useParams();
  const navigate = useNavigate();
  const webinarId = params.id ?? "";

  const [state, dispatch] = useReducer(boardReducer, BLANK);
  const [server, setServer] = useState<BoardStateResponse | null>(null);
  const [phase, setPhase] = useState<BoardPhase | "loading">("loading");
  /** The recording's size, known when THIS tab picked the talk. */
  const [sizeBytes, setSizeBytes] = useState<number | null>(null);
  /** The talk's own length, once the pipeline has probed the recording. */
  const [durationSec, setDurationSec] = useState<number | null>(null);
  /** "10 new video hours added for $39.", when this upload needed a top-up. */
  const [receipt, setReceipt] = useState("");
  /** Why the recording stopped, in the server's words, when the host can fix it. */
  const [stopped, setStopped] = useState("");
  /**
   * The board a finished read produced, held while the waiting stage plays out
   * the scenes that are left. The board opens when the stage says every scene has
   * been seen, so it never cuts away in the middle of one.
   */
  const [finish, setFinish] = useState<BoardStateResponse | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<BoardBusy | null>(null);
  const [message, setMessage] = useState("");
  const [opError, setOpError] = useState("");
  const [entryError, setEntryError] = useState("");
  const [readError, setReadError] = useState("");
  const [listenError, setListenError] = useState("");
  const [publishState, setPublishState] = useState<"idle" | "sending" | "done">("idle");
  const [publishError, setPublishError] = useState("");
  const [media, setMedia] = useState<{ duration: number; poster: string | null; url: string | null; token: string | null; known: boolean }>({
    duration: 0,
    poster: null,
    token: null,
    url: null,
    known: false,
  });
  const [registerUrl, setRegisterUrl] = useState("");
  /** Every follow-up opened on this board, by row: its editor, and what the
   *  row last accepted. Drafts survive a switch to another card; only Done
   *  writes them. */
  const [follows, setFollows] = useState<Record<string, FollowState>>({});
  const [followTriggers, setFollowTriggers] = useState<ReadonlyMap<string, string>>(new Map());
  const [followRows, setFollowRows] = useState<ReadonlyMap<string, Pick<AutomationSummary, "name" | "trigger" | "graph" | "rooms">>>(new Map());
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  /** What the producer could not do on the read, held here: a later refresh of
   *  the board does not carry them, and they are the host's to dismiss. */
  const [warnings, setWarnings] = useState<string[]>([]);
  /** The card an operation just wrote, to be brought into view once React has
   *  actually put it on the screen. */
  const [flushAt, setFlushAt] = useState(0);
  const layout = useLayout();
  /** The board was just made on this screen: a phone says so, with Walk it through, before the walk. */
  const [made, setMade] = useState(false);
  const toast = useToast();
  const say = useCallback(
    (text: string, action?: ToastAction) => toast(action ? { text, action: { label: action.label, onClick: action.run } } : text),
    [toast],
  );
  /** The chapters as the host left them on step 6, held until the design is asked for. */
  const [asked, setAsked] = useState<{ rows: ChapterRow[]; version: number } | null>(null);
  /** What the host said the page should feel like, shown over the first pair. */
  const [feel, setFeel] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState("");
  const brandTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stateRef = useRef(state);
  stateRef.current = state;
  const followsRef = useRef<Record<string, FollowState>>(follows);
  followsRef.current = follows;
  const reading = useRef(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const freshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gone = useRef(false);
  /** The latest `refresh`, so the save queue can call it without holding a stale one. */
  const refreshRef = useRef<() => Promise<void>>(async () => undefined);
  /** The board asks for a transcript at most once per attempt. */
  const transcribeAsked = useRef(false);
  /** The talk's length is asked for ONCE; a 0 answer is not asked for again. */
  const durationAsked = useRef(false);
  /** The read that has landed, for the poll running behind the closing stage. */
  const finishRef = useRef<BoardStateResponse | null>(null);
  /** One producer operation at a time: two would leave on one version. */
  const working = useRef(false);
  /** The host's changes made while an operation is on its way, laid over its answer when it lands. */
  const madeMeanwhile = useRef<BoardAction[] | null>(null);

  /** The one writer. Built once per board; every change goes through it. */
  const queue = useMemo(
    () =>
      new BoardSaveQueue({
        webinarId,
        version: 0,
        api: {
          save: (id, body, init) => api.board.save(id, body, init),
          get: (id) => api.board.get(id),
        },
        hooks: {
          onSaved: () => {
            setMessage("");
            // What the server now says about this board (what publishing is
            // waiting on, above all) is only worth asking for once typing stops.
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
            refreshTimer.current = setTimeout(() => void refreshRef.current(), REFRESH_MS);
          },
          onReloaded: (fromServer) => {
            setServer(fromServer);
            dispatch({ type: "adopt", state: fromServer.state });
          },
          onMessage: (m) => setMessage(m),
        },
      }),
    [webinarId],
  );

  /** Take a whole board from the server: its state, its version, its verdicts. */
  const adopt = useCallback(
    (fresher: BoardStateResponse) => {
      queue.reset(fresher.state, fresher.version);
      dispatch({ type: "adopt", state: fresher.state });
      setServer(fresher);
    },
    [queue],
  );

  /** Mark the cards a read or a rewrite changed, for a moment. */
  const markFresh = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    setFresh(new Set(ids));
    if (freshTimer.current) clearTimeout(freshTimer.current);
    freshTimer.current = setTimeout(() => setFresh(new Set()), FRESH_MS);
  }, []);

  /** What the server now thinks of the board we are holding. */
  const refresh = useCallback(async () => {
    if (gone.current || !webinarId) return;
    try {
      const fresher = await api.board.get(webinarId);
      if (gone.current) return;
      if (fresher.version !== queue.version) {
        adopt(fresher);
        setMessage(BOARD_RELOADED);
        return;
      }
      setServer(fresher);
    } catch {
      // A refresh that does not land changes nothing: the next save asks again.
    }
  }, [adopt, queue, webinarId]);
  refreshRef.current = refresh;

  /** The triggers every follow-up on this board runs on (so the library de-dupes). */
  const loadTriggers = useCallback(async () => {
    try {
      const list = await api.automations.list();
      if (gone.current) return;
      setFollowTriggers(
        new Map(list.automations.map((a) => [a.id, a.library_kind ?? ""] as const)),
      );
      setFollowRows(new Map(list.automations.map((a) => [a.id, { name: a.name, trigger: a.trigger, graph: a.graph, rooms: a.rooms ?? [] }] as const)));
    } catch {
      // Without it the library simply offers everything, as it did before.
    }
  }, []);

  // ── the recording ──────────────────────────────────────────────────────
  /** A talk picked in Assets becomes this room's video, and the room starts making it playable. */
  const useTalk = useCallback(
    async (talk: PickedTalk) => {
      setEntryError("");
      setSizeBytes(talk.size || null);
      transcribeAsked.current = false;
      setListenError("");
      try {
        await api.webinars.videoFromAsset(webinarId, talk.id);
        if (gone.current) return;
        setPhase("playable");
      } catch (e) {
        if (gone.current) return;
        setPhase("entry");
        setEntryError(e instanceof ApiError ? e.message : "That talk could not be used. Try again.");
      }
    },
    [webinarId],
  );

  /**
   * Take the board a read produced. Called when the waiting stage has played its
   * last scene, so the host sees the work land before the board replaces it.
   */
  const openBoard = useCallback(
    (fresher: BoardStateResponse) => {
      finishRef.current = null;
      setFinish(null);
      setPhase("board");
      setSelected(null);
      markFresh([...fresher.state.page, ...fresher.state.room, ...fresher.state.mail].map((c) => c.id));
      say(
        fresher.scheduleMode === "live"
          ? "Written. Everything on the board came from your notes."
          : "Read. Everything on the board came from the talk.",
      );
      setWarnings(warningsOf(fresher));
      void loadTriggers();
    },
    [loadTriggers, markFresh, say],
  );

  /**
   * The read has landed and the stage has shown it. A first read goes on to the
   * fifth step, where the page it wrote is used; a read with no
   * pages (the static flow, or a read again) opens the board.
   */
  const landRead = useCallback(
    (fresher: BoardStateResponse) => {
      const next = resumePhase(fresher);
      if (next === "board") {
        openBoard(fresher);
        return;
      }
      finishRef.current = null;
      setFinish(null);
      setPhase(next);
    },
    [openBoard],
  );

  /** One pass over the talk, once. A board that has been read never asks again. */
  const runRead = useCallback(
    async (force: boolean) => {
      if (!webinarId) return;
      reading.current = true;
      setReadError("");
      try {
        const fresher = await api.board.read(webinarId, force ? { force: true } : {});
        if (gone.current) return;
        adopt(fresher);
        // The read is done, but the stage may still owe the host the last scenes:
        // the cards it just wrote are the tenth one. It opens the board itself.
        finishRef.current = fresher;
        setFinish(fresher);
      } catch (e) {
        if (gone.current) return;
        if (e instanceof ApiError && e.status === 409) {
          // Somebody else read it, or it was read already: take what is there.
          const fresher = await api.board.get(webinarId).catch(() => null);
          if (fresher) {
            adopt(fresher);
            if (resumePhase(fresher) !== "board") {
              setPhase(fresher.scheduleMode === "live" ? "write" : "build");
            } else {
              finishRef.current = fresher;
              setFinish(fresher);
            }
          }
          return;
        }
        // A read that failed is NOT retried on the next poll: one refusal is one
        // refusal, not a loop of them. The host asks again when they are ready.
        setReadError(e instanceof ApiError ? e.message : "AI Studio could not read that talk.");
      } finally {
        reading.current = false;
      }
    },
    [adopt, webinarId],
  );

  /** Ask for the transcript ourselves, when the recording's own read did not happen. */
  const askForTranscript = useCallback(
    async (videoId: string) => {
      transcribeAsked.current = true;
      const outcome = await runTranscribe(api.board, webinarId, videoId);
      if (gone.current) return;
      if (!outcome.ok) setListenError(outcome.error);
    },
    [webinarId],
  );

  // ── first paint: the server decides the screen ─────────────────────────
  useEffect(() => {
    gone.current = false;
    if (!webinarId) return;
    let live = true;
    void (async () => {
      try {
        const fresher = await api.board.get(webinarId);
        if (!live) return;
        const next = resumePhase(fresher);
        setServer(fresher);
        if (next === "board") {
          adopt(fresher);
          setWarnings(warningsOf(fresher));
          void loadTriggers();
        }
        setPhase(next);
        if (next === "entry") {
          const waiting = takeTalk(webinarId);
          if (waiting) void useTalk(waiting);
        }
      } catch (e) {
        if (!live) return;
        setPhase("entry");
        setEntryError(e instanceof ApiError ? e.message : "We could not open that draft.");
      }
    })();
    return () => {
      live = false;
      gone.current = true;
      // Everything this tab still holds goes out before it stops: the board it
      // was about to save and the follow-up words typed a moment ago. The flush
      // sends from this very tick, so `stop` below can never beat it.
      void queue.flush(true, true);
      queue.stop();
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      if (freshTimer.current) clearTimeout(freshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarId]);

  // ── while it is encoding, transcribing or being read ───────────────────
  useEffect(() => {
    if (!keepPolling(phase, server)) return;
    let live = true;
    const tick = async () => {
      // The stage is playing out a read that has already landed: there is nothing
      // left to learn, and the board it produced is already in hand.
      if (finishRef.current) return;
      // While the producer is reading, the board it is about to write is the one
      // that counts: a poll landing mid-read must not put the old one back. The
      // steps it has landed still come through, though, because THIS tab is the
      // one running the read and the stage would otherwise never move.
      if (reading.current) {
        const during = await api.board.get(webinarId).catch(() => null);
        if (during && live && reading.current && !gone.current) {
          setServer((s) => (s ? { ...s, readProgress: during.readProgress } : s));
        }
        return;
      }
      try {
        const fresher = await api.board.get(webinarId);
        if (!live || reading.current) return;
        setServer(fresher);
        const next = resumePhase(fresher);
        if (next === "board") {
          adopt(fresher);
          // Mid-read on this screen, the stage owes the host its last scenes.
          if (phase === "build" || phase === "write") {
            finishRef.current = fresher;
            setFinish(fresher);
            return;
          }
          openBoard(fresher);
          return;
        }
        setPhase(next);
        // The talk's own length, asked for once the pipeline has probed the
        // recording: the stage scales its estimate to it and counts the minutes
        // heard against it, and neither is a number this tab may invent.
        // ONE ask, ever: the probe writes the length before the first chunk is
        // encoded, so by `transcoding` the answer is there, and asking again every
        // beat and a half for something that is not going to change is noise.
        if (!durationAsked.current && PROBED_STATES.includes(fresher.video.state ?? "")) {
          durationAsked.current = true;
          const status = await api.webinars.videoStatus(webinarId).catch(() => null);
          if (status && status.duration_sec > 0 && !gone.current) {
            setDurationSec(status.duration_sec);
          }
          // By now the upload's video hours are paid for, so its receipt is known.
          if (status?.topup && !gone.current) setReceipt(uploadReceipt(status.topup));
        }
        // The recording is playable and there is nothing to read: its own
        // transcribe step did not happen, so the board asks for one. Once.
        if (needsTranscribe(fresher, transcribeAsked.current) && !listenError) {
          void askForTranscript(fresher.video.videoId ?? "");
        }
        // A live's page is read from its notes the moment its board opens.
        if ((next === "build" || next === "write") && !readError && !reading.current) void runRead(false);
      } catch {
        // Keep waiting: a poll that fails is not a draft that failed.
      }
    };
    // A hidden tab is a tab nobody is watching: it asks for nothing until it is
    // looked at again, and then it asks straight away rather than making the host
    // wait out an interval for a screen that is already stale.
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void tick(), POLL_MS);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      void tick();
      start();
    };
    if (!document.hidden) {
      void tick();
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      live = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, webinarId, readError, listenError, server?.video.state, server?.transcriptPresent]);

  // A recording that stopped on a declined card says so, not that the render failed.
  const failedNow = videoFailed(server);
  useEffect(() => {
    if (!failedNow || !webinarId) return;
    let live = true;
    api.webinars
      .videoStatus(webinarId)
      .then((status) => live && setStopped(status.stopped ?? ""))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [failedNow, webinarId]);

  // The recording is ready but the strip has no wave to draw: ask the server
  // to draw the stills again, once, and take whatever comes back. A failure is
  // silence, not a message: the row simply keeps its frames and its beads.
  useEffect(() => {
    if (phase !== "board" || !webinarId) return;
    if (server?.video.state !== "ready" || server.video.waveUrl) return;
    if (STILLS_ASKED.has(webinarId)) return;
    STILLS_ASKED.add(webinarId);
    let live = true;
    void (async () => {
      const stills = await api.webinars.videoStills(webinarId).catch(() => null);
      if (!live || !stills) return;
      setServer((s) =>
        s
          ? {
              ...s,
              video: {
                ...s.video,
                posterUrl: stills.posterUrl,
                frames: stills.frames,
                waveUrl: stills.waveUrl,
              },
            }
          : s,
      );
    })();
    return () => {
      live = false;
    };
  }, [phase, webinarId, server?.video.state, server?.video.waveUrl]);

  // ── the talk itself, once there is a board ─────────────────────────────
  useEffect(() => {
    if (phase !== "board" || !webinarId) return;
    let live = true;
    void (async () => {
      const [video, detail] = await Promise.all([
        api.webinars.videoStatus(webinarId).catch(() => null),
        api.webinars.get(webinarId).catch(() => null),
      ]);
      if (!live) return;
      setMedia((m) =>
        video
          ? { duration: video.duration_sec, poster: video.poster_url, url: video.hls_url, token: video.media_token ?? null, known: true }
          : { ...m, known: true },
      );
      if (detail) setRegisterUrl(detail.register_url);
    })();
    return () => {
      live = false;
    };
  }, [phase, webinarId]);

  // ── every change is written, debounced ─────────────────────────────────
  useEffect(() => {
    if (phase !== "board") return;
    queue.queue(state);
  }, [state, phase, queue]);

  // Something asked to be saved now (the address, say). This effect is declared
  // after the one above, so the change is queued before it is flushed.
  useEffect(() => {
    if (flushAt === 0) return;
    void queue.flush();
  }, [flushAt, queue]);

  // A tab going away writes the board it holds; a tab coming back asks whether
  // it is still the one holding the pen. Follow-up words are never written on
  // the way out: Done is the write, and leaving with unsaved words is asked
  // about below.
  useEffect(() => {
    const leave = (race: boolean) => {
      void queue.flush(true, race);
    };
    // `pagehide` is the end: race whatever is in flight, because there is no
    // later. A tab switch is not the end: wait for the in-flight write so the
    // newer board goes out on the version it produced.
    const flush = () => leave(true);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") leave(false);
      else void refreshRef.current();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [queue]);

  // ── the follow-up the host has open ────────────────────────────────────
  const openCard = selected ? findCard(state, selected) : undefined;
  const openAutomationId =
    openCard && openCard.kind === "mail" && openCard.automationId ? openCard.automationId : "";

  /** Done: write one follow-up's row from its editor, whole. */
  const saveFollow = useCallback(async (id: string) => {
    const held = followsRef.current[id];
    if (!held?.editor) return;
    const sent = held.editor;
    setFollows((m) => (m[id] ? { ...m, [id]: { ...m[id], saving: true } } : m));
    const written = await writeFollowUp(api.automations, id, sent, held.saved ?? null);
    if (gone.current) return;
    if (!written.detail) {
      setFollows((m) => (m[id] ? { ...m, [id]: { ...m[id], saving: false, error: written.error } } : m));
      return;
    }
    const editor = editorFromDetail(written.detail);
    // Words typed while the write was out are newer than what went out: they
    // stay, and Done stays lit for them. Only an untouched editor takes the
    // server's copy.
    setFollows((m) => {
      const f = m[id];
      if (!f) return m;
      return { ...m, [id]: { ...f, editor: f.editor === sent ? editor : f.editor, saved: editor, saving: false, error: "" } };
    });
    const email = emailOf(editor);
    const card = stateRef.current.mail.find((c) => c.kind === "mail" && c.automationId === id);
    if (email && card && card.kind === "mail" && card.subject !== email.subject) {
      dispatch({ type: "replace", card: { ...card, subject: email.subject } });
    }
  }, []);

  const loadFollow = useCallback(async (automationId: string) => {
    setFollows((m) => ({
      ...m,
      [automationId]: { automationId, editor: null, saved: null, error: "", saving: false },
    }));
    try {
      const detail = await api.automations.get(automationId);
      if (gone.current) return;
      const editor = editorFromDetail(detail);
      setFollows((m) => (m[automationId] ? { ...m, [automationId]: { ...m[automationId], editor, saved: editor } } : m));
    } catch {
      if (gone.current) return;
      setFollows((m) =>
        m[automationId] ? { ...m, [automationId]: { ...m[automationId], error: "We could not open that follow-up." } } : m,
      );
    }
  }, []);

  // Opening a follow-up loads its row once; the ones opened before keep their
  // drafts, Done or not, until the board is left.
  useEffect(() => {
    if (!openAutomationId || followsRef.current[openAutomationId]) return;
    void loadFollow(openAutomationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAutomationId]);

  const follow = openAutomationId ? (follows[openAutomationId] ?? null) : null;
  const unsavedFollowUps = useMemo(
    () => new Set(Object.values(follows).filter(followDirty).map((f) => f.automationId)),
    [follows],
  );

  // Leaving the page with words no Done has written: the browser asks first.
  useEffect(() => {
    if (unsavedFollowUps.size === 0) return;
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [unsavedFollowUps]);

  const followUp: FollowUpBinding | null = follow
    ? {
        automationId: follow.automationId,
        editor: follow.editor,
        error: follow.error,
        saving: follow.saving,
        dirty: followDirty(follow),
        setEmail: (patch) => {
          const id = follow.automationId;
          setFollows((m) => (m[id]?.editor ? { ...m, [id]: { ...m[id], editor: withEmail(m[id].editor as Editor, patch) } } : m));
        },
        setEditor: (next) => {
          const id = follow.automationId;
          setFollows((m) => {
            const f = m[id];
            if (!f) return m;
            const editor = typeof next === "function" && f.editor ? next(f.editor) : (next as Editor);
            return editor ? { ...m, [id]: { ...f, editor } } : m;
          });
        },
        save: () => saveFollow(follow.automationId),
        discard: () => {
          const id = follow.automationId;
          setFollows((m) => (m[id]?.saved ? { ...m, [id]: { ...m[id], editor: m[id].saved, error: "" } } : m));
        },
      }
    : null;

  // ── the producer's own operations ──────────────────────────────────────
  const runOp = useCallback(
    async (body: Omit<BoardCardRequest, "version">, doing: BoardBusy, replaceCardId?: string): Promise<BoardCard | null> => {
      // One at a time: a second operation would be sent on the version the first
      // is about to replace, and one of the two answers would be thrown away.
      if (!webinarId || working.current) return null;
      working.current = true;
      setBusy(doing);
      setOpError("");
      // Our own edits go first, so the version we hand over is the one the
      // server has: an operation never lands on a board the host has moved past.
      // A change made while that write was on its way goes too, before the ask.
      for (let tries = 0; tries < FLUSH_TRIES && (tries === 0 || queue.dirty); tries++) await queue.flush();
      if (gone.current) return null;
      if (queue.dirty) {
        setOpError(NOT_SAVED_YET);
        setBusy(null);
        working.current = false;
        return null;
      }
      // Why: a change made while Flash works would leave on the version its answer replaces, and be lost or refused
      // as "changed somewhere else". It waits, and is laid over the answer (specs/formal/board-edits).
      queue.hold();
      madeMeanwhile.current = [];
      const outcome = await runCardOp(api.board, webinarId, queue.version, body);
      const made = madeMeanwhile.current;
      madeMeanwhile.current = null;
      if (gone.current) return null;
      if (outcome.kind === "reloaded") {
        // Another tab wrote first: the board it wrote is taken, as any reload is, and the host is told.
        adopt(outcome.server);
        queue.release();
        setMessage(BOARD_RELOADED);
        setBusy(null);
        working.current = false;
        return null;
      }
      if (outcome.kind === "refused") {
        queue.release();
        setOpError(outcome.error);
        setBusy(null);
        working.current = false;
        return null;
      }
      queue.reset(outcome.state, outcome.version);
      dispatch({ type: "adopt", state: outcome.state });
      for (const action of made) dispatch(action);
      queue.release();
      const card: BoardCard | undefined = outcome.card;
      if (card) {
        // A new card is said, unless the host is about to write it.
        if (body.op !== "fromTag") say(`${card.label}, from the talk.`);
        setSelected(card.id);
        markFresh([card.id]);
        // A rewritten follow-up is a rewritten ROW: the copy on screen has to
        // come back from it, or the next keystroke would write the old words.
        if (card.kind === "mail" && card.automationId) {
          void loadFollow(card.automationId);
          void loadTriggers();
        }
      }
      if (replaceCardId) dispatch({ type: "delete", id: replaceCardId });
      setBusy(null);
      working.current = false;
      void refreshRef.current();
      return card ?? null;
    },
    [adopt, loadFollow, loadTriggers, markFresh, queue, say, webinarId],
  );

  const host: BoardHost = {
    webinarId,
    server:
      server ?? {
        state,
        version: 0,
        readAt: null,
        namesChecked: false,
        chaptersChecked: false,
        readProgress: null,
        transcriptPresent: false,
        video: { state: null, chunksDone: 0, chunkCount: 0, videoId: null },
        title: state.workingTitle,
        slug: state.slug,
        status: "draft",
        hostName: "",
        hostAvatarUrl: null,
        brand: { accent: "#7a2338", brass: "#b0873a", paper: "#f6f2f2", logo_url: null },
        publishBlocks: [],
        pageHtml: null,
        pagePairs: 0,
      },
    selected,
    duration: media.duration,
    lengthKnown: media.known,
    poster: media.poster,
    busy,
    followUp,
    followUpTriggers: followTriggers,
    followUpRows: followRows,
    fresh,
    warnings,
    dismissWarnings: () => setWarnings([]),
    message,
    opError,
    dispatch: (action) => {
      madeMeanwhile.current?.push(action);
      dispatch(action);
    },
    select: (id) => {
      setSelected(id);
      setOpError("");
    },
    toast: (text: string, action?: ToastAction) => say(text, action),
    addMomentAt: (kind: MomentKind, ts: string) => runOp({ op: "moment", kind, ts }, { op: "moment", cardId: null }),
    addFollowUp: (words: string, replaceCardId?: string, label?: string) =>
      void runOp(
        { op: "followup", words, ...(label ? { label } : {}) },
        { op: "followup", cardId: null },
        replaceCardId,
      ),
    startFollowUp: () => runOp({ op: "fromTag" }, { op: "followup", cardId: null }),
    saveSlug: (slug: string) => {
      dispatch({ type: "slug", slug });
      setFlushAt((n) => n + 1);
    },
    readAgain: () => {
      // The guard goes up before the flush, so the poll running behind this does
      // not put the old board back while the new read is on its way.
      reading.current = true;
      finishRef.current = null;
      setFinish(null);
      // A live's page is written again from its notes, on its own waiting stage.
      setPhase(server?.scheduleMode === "live" ? "write" : "build");
      setReadError("");
      void queue.flush().then(() => {
        reading.current = false;
        return runRead(true);
      });
    },
    retryRender: () => {
      setEntryError("");
      void api.webinars
        .retryVideo(webinarId)
        .then(() => {
          setServer((s) => (s ? { ...s, video: { ...s.video, state: "transcoding" } } : s));
          setPhase("playable");
        })
        .catch((e: unknown) =>
          setEntryError(e instanceof ApiError ? e.message : "That render did not start."),
        );
    },
    uploadAgain: () => {
      setPhase("entry");
      setEntryError("");
    },
    publish: () => {
      setPublishState("sending");
      setPublishError("");
      void (async () => {
        await queue.flush();
        try {
          const res = await api.board.publish(webinarId);
          if (gone.current) return;
          queue.version = res.version;
          setPublishState("done");
          say(
            server?.live ? "Published: the page is live." : "Published: page live, room armed, follow-ups on.",
          );
          const detail = await api.webinars.get(webinarId).catch(() => null);
          if (detail) setRegisterUrl(detail.register_url);
          await refreshRef.current();
        } catch (e) {
          if (gone.current) return;
          setPublishState("idle");
          setPublishError(e instanceof ApiError ? e.message : "That did not publish.");
        }
      })();
    },
    unpublish: () => {
      void (async () => {
        try {
          await api.board.unpublish(webinarId);
          if (gone.current) return;
          setPublishState("idle");
          say("The page is private again. Publish when you want it back.");
          await refreshRef.current();
        } catch (e) {
          if (gone.current) return;
          say(e instanceof ApiError ? e.message : "That did not work.");
        }
      })();
    },
    moveLive: async (when, repeats) => {
      const live = await moveLiveTime(webinarId, when, repeats);
      if (typeof live === "string") return live;
      if (gone.current) return "";
      setServer((s) => (s ? { ...s, live } : s));
      say(timeSavedLine(live));
      return "";
    },
    liveChanged: (live, said) => {
      if (gone.current) return;
      setServer((s) => (s ? { ...s, live } : s));
      say(said);
    },
    publishState,
    publishError,
    liveUrl: registerUrl,
  };

  // ── the steps before the board ─────────────────────────────────────────
  const minutes = durationSec ? Math.max(1, Math.round(durationSec / 60)) : 0;
  const live = server?.scheduleMode === "live";
  const chapterCount = server ? chapterRowsOf(server.state).length : 0;
  const recLines = [
    sizeBytes ? `${sizeLabel(sizeBytes)}, all here` : "All here",
    server?.video.chunkCount ? `${server.video.chunkCount} pieces, all ready` : "All ready",
    minutes ? `${minutes} minutes, every word` : "Every word",
    "Names checked",
    "Page, chapters, moments, follow-ups",
    chapterCount ? `${chapterCount} chapters` : "Looked over",
    "Page chosen",
  ];
  const talk: RailTalk | null =
    server && (server.video.posterUrl || sizeBytes)
      ? {
          title: server.title || state.workingTitle || "Your talk",
          meta: [sizeBytes ? sizeLabel(sizeBytes) : "", durationSec ? mmss(durationSec) : ""].filter(Boolean).join(" · "),
          poster: server.video.posterUrl ?? null,
        }
      : null;
  const finishLater = () => {
    navigate("/rooms");
    toast("Saved as a draft");
  };
  const pageChosen = () => {
    void api.board.get(webinarId).then((fresher) => {
      if (gone.current) return;
      adopt(fresher);
      openBoard(fresher);
      setMade(layout !== "computer");
    });
  };
  const brand: BrandColours = server ? { accent: server.brand.accent, brass: server.brand.brass, paper: server.brand.paper } : { accent: "#7a2338", brass: "#b0873a", paper: "#f6f2f2" };
  // The colours are the business's: changed here, they change everywhere, a moment after the last pick.
  const changeBrand = (next: BrandColours) => {
    setServer((s) => (s ? { ...s, brand: { ...s.brand, ...next } } : s));
    if (brandTimer.current) clearTimeout(brandTimer.current);
    brandTimer.current = setTimeout(() => {
      void api.settings.updateBrand(next).catch(() => toast({ text: "Those colours did not save. Try again.", icon: "info" }));
    }, 500);
  };
  // "Design my page": the pictures and the feel go on the draft first, so the first page is written from them,
  // then the chapters as checked, which is what starts the page designer.
  const startDesign = async (ask: DesignAsk, held = asked) => {
    if (!held) return;
    setAskBusy(true);
    setAskError("");
    try {
      await api.pages.inspirations(webinarId, { ask: ask.ask || null, inspirations: ask.inspirations });
      await api.board.chapters(webinarId, { version: held.version, rows: held.rows });
      const fresher = await api.board.get(webinarId);
      if (gone.current) return;
      setFeel(ask.ask);
      setServer(fresher);
      setAsked(null);
      setPhase(resumePhase(fresher));
    } catch (e) {
      if (gone.current) return;
      setAskError(e instanceof ApiError ? e.message : "The design could not start. Try again.");
    } finally {
      if (!gone.current) setAskBusy(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="page wide nbwizpage">
        <div className="card nbw nbload" aria-busy="true" aria-label="Opening the room" />
      </div>
    );
  }

  if (layout !== "computer") {
    if (phase === "board") {
      return made ? (
        <SeasonReady layout={layout} live={live} server={host.server} state={state} durationSec={durationSec} onWalk={() => setMade(false)} />
      ) : (
        <SeasonWalk layout={layout} state={state} host={host} />
      );
    }
    const failed = videoFailed(server);
    const working = phase === "playable" || phase === "listen";
    const workError = listenError || entryError || (failed ? stopped || VIDEO_ERROR_MSG : "");
    const error = working ? workError : phase === "entry" ? entryError : phase === "build" || phase === "write" ? readError : askError;
    const retryRead = () => void runRead(false);
    const action = working
      ? failed
        ? { label: "Try the render again", onClick: host.retryRender }
        : listenError
          ? {
              label: "Try listening again",
              onClick: () => {
                setListenError("");
                transcribeAsked.current = false;
              },
            }
          : undefined
      : readError && (phase === "build" || phase === "write")
        ? { label: "Try again", onClick: retryRead }
        : undefined;
    return (
      <SeasonMake
        layout={layout}
        phase={phase}
        live={live}
        webinarId={webinarId}
        server={server}
        durationSec={durationSec}
        receipt={receipt}
        error={error}
        action={action}
        second={working && failed ? { label: "Upload a different recording", onClick: host.uploadAgain } : undefined}
        built={finish?.state ?? null}
        onBuilt={() => finish && landRead(finish)}
        entryBusy={false}
        onTalk={(a) => void useTalk({ id: a.id, name: a.name, size: a.size_bytes })}
        onFinishLater={finishLater}
        onNamesChecked={() => {
          // The gate is open: the next poll sees namesChecked, and the read fires as it always has.
          void api.board.get(webinarId).then((fresher) => {
            if (gone.current) return;
            setServer(fresher);
            setPhase(resumePhase(fresher));
          });
        }}
        onChapters={(rows, version) => {
          const held = { rows, version };
          setAsked(held);
          void startDesign({ ask: "", inspirations: {} }, held);
        }}
        designing={askBusy}
        onChosen={pageChosen}
      />
    );
  }

  if (phase === "entry") {
    return (
      <MakeFrame kind="rec" current={0} reached={0} lines={recLines} started onFinishLater={finishLater}>
        <StepUpload onTalk={(a) => void useTalk({ id: a.id, name: a.name, size: a.size_bytes })} error={entryError} />
      </MakeFrame>
    );
  }

  // A live room's own steps: its page written from the notes, then designed.
  if (phase === "write" || (phase === "design" && live)) {
    return (
      <MakeFrame
        kind="live"
        current={phase === "write" ? 2 : 3}
        reached={phase === "write" ? 2 : 3}
        lines={["When", "Your notes", "Title, description, what we will cover", "Page chosen"]}
        started
        onFinishLater={finishLater}
      >
        {phase === "write" ? (
          <StepWrite
            readProgress={server?.readProgress ?? null}
            built={finish?.state ?? null}
            onNext={() => finish && landRead(finish)}
            error={readError || undefined}
            action={readError ? { label: "Try again", onClick: () => void runRead(false) } : undefined}
          />
        ) : (
          <DesignChoice webinarId={webinarId} n={4} of={4} live feel={feel} onChosen={pageChosen} />
        )}
      </MakeFrame>
    );
  }

  if (phase !== "board") {
    const failed = videoFailed(server);
    const order = ["upload", "playable", "listen", "names", "build", "chapters", "design"] as const;
    const at = asked ? 6 : order.indexOf(phase);
    const workError = listenError || entryError || (failed ? stopped || VIDEO_ERROR_MSG : "") || undefined;
    return (
      <MakeFrame kind="rec" current={at} reached={at} lines={recLines} talk={talk} started onFinishLater={finishLater}>
        {receipt ? (
          <p className="note good nbreceipt">
            <span>{receipt}</span>
          </p>
        ) : null}
        {phase === "playable" || phase === "listen" ? (
          <StepWork
            step={phase}
            sizeBytes={sizeBytes}
            video={server?.video ?? { state: null, chunksDone: 0, chunkCount: 0, videoId: null }}
            durationSec={durationSec}
            heard={server?.heardLines ?? []}
            error={workError}
            action={
              failed
                ? { label: "Try the render again", onClick: host.retryRender }
                : listenError
                  ? {
                      label: "Try listening again",
                      onClick: () => {
                        setListenError("");
                        transcribeAsked.current = false;
                      },
                    }
                  : undefined
            }
            second={failed ? { label: "Upload a different recording", onClick: host.uploadAgain } : undefined}
          />
        ) : phase === "names" ? (
          <StepNames
            webinarId={webinarId}
            minutes={minutes}
            onChecked={() => {
              // The gate is open: the next poll sees namesChecked, and the read fires as it always has.
              void api.board.get(webinarId).then((fresher) => {
                if (gone.current) return;
                setServer(fresher);
                setPhase(resumePhase(fresher));
              });
            }}
          />
        ) : phase === "build" ? (
          <StepBuild
            readProgress={server?.readProgress ?? null}
            built={finish?.state ?? null}
            onNext={() => finish && landRead(finish)}
            error={readError || undefined}
            action={readError ? { label: "Try the read again", onClick: () => void runRead(false) } : undefined}
          />
        ) : phase === "chapters" && !asked ? (
          <StepChapters webinarId={webinarId} onNext={(rows, version) => setAsked({ rows, version })} />
        ) : asked ? (
          <DesignAskCard webinarId={webinarId} n={7} of={7} live={false} colours={brand} onColours={changeBrand} onStart={(ask) => void startDesign(ask)} busy={askBusy} error={askError || undefined} />
        ) : (
          <DesignChoice webinarId={webinarId} n={7} of={7} live={false} feel={feel} onChosen={pageChosen} />
        )}
      </MakeFrame>
    );
  }

  return (
    <WalkScreen state={state} host={host} />
  );
}
